import React, { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { authApi } from "../../lib/api";
import { Lock, Mail, ArrowRight, Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import AdminMfaGate from "./AdminMfaGate";

const AdminLogin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  // MFA gate state: null | "mfa_setup_required" | "mfa_required"
  const [mfaStatus, setMfaStatus] = useState(null);

  if (!loading && user) return <Navigate to="/admin" replace />;

  // When MFA is pending, show the gate instead of the login form.
  if (mfaStatus) {
    return <AdminMfaGate initialStatus={mfaStatus} onCancel={() => setMfaStatus(null)} />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    try {
      // Backend now returns { status: "mfa_setup_required" | "mfa_required" }
      // without setting the admin session cookie. Session is granted only
      // after /auth/mfa/verify.
      const res = await authApi.login(email, password);
      const status = res?.data?.status;
      if (status === "mfa_setup_required" || status === "mfa_required") {
        setMfaStatus(status);
      } else {
        // Unexpected shape — safest to reload
        navigate("/admin");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Accesso non riuscito");
    } finally {
      setSubmitting(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError(null); setResetSent(false);
    try {
      const redirectTo = `${window.location.origin}/admin/login`;
      await authApi.requestPasswordReset(email, redirectTo);
      setResetSent(true);
    } catch (err) {
      setResetSent(true); // do not leak existence
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#F8F9FA]">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-2xl bg-[#12964A] flex items-center justify-center text-white font-display text-xl">C</div>
          <div className="leading-tight">
            <div className="font-display font-bold text-lg text-[#1F2937]">CCN Sanluri</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Area riservata</div>
          </div>
        </Link>

        <div className="bg-white rounded-3xl p-8 md:p-10 card-shadow">
          <h1 className="font-display text-3xl font-bold text-[#1F2937] mb-2">
            {resetMode ? "Recupera password" : "Accedi al pannello"}
          </h1>
          <p className="text-slate-500 mb-8">
            {resetMode ? "Ti invieremo un link email per reimpostarla." : "Inserisci le tue credenziali amministratore."}
          </p>

          {error && (
            <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>
          )}

          {resetSent && (
            <div className="mb-6 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 mt-0.5" /> Se l&apos;email è registrata riceverai un link per reimpostare la password.
            </div>
          )}

          <form onSubmit={resetMode ? sendReset : submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Email</label>
              <div className="flex items-center gap-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4">
                <Mail className="w-4 h-4 text-slate-400" />
                <input
                  type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="info@sanluriccn.it"
                  className="w-full bg-transparent focus:outline-none py-3.5"
                />
              </div>
            </div>
            {!resetMode && (
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Password</label>
                <div className="flex items-center gap-3 bg-[#F8F9FA] border border-[#E5E7EB] rounded-2xl px-4">
                  <Lock className="w-4 h-4 text-slate-400" />
                  <input
                    type="password" required minLength={6}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-transparent focus:outline-none py-3.5"
                  />
                </div>
              </div>
            )}
            <button
              type="submit" disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-70 text-white rounded-2xl py-4 font-medium transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (resetMode ? <KeyRound className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />)}
              {resetMode ? "Invia link di recupero" : "Accedi"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button type="button" onClick={() => { setResetMode((v) => !v); setError(null); setResetSent(false); }} className="text-slate-500 hover:text-[#12964A]">
              {resetMode ? "← Torna al login" : "Password dimenticata?"}
            </button>
            <Link to="/" className="text-slate-500 hover:text-[#12964A]">← Al sito</Link>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          L&apos;accesso è riservato agli amministratori del Centro Commerciale Naturale di Sanluri.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
